/* begin general import */
import { Col, Row, notification } from "antd";
import { ASSETS_SVG } from "config/consts";
import { UserManual } from "models/UserManual";
import React from "react";
import { useTranslation } from "react-i18next";
import { Model } from "react3l-common";
import Drawer, {
  DrawerProps,
} from "react3l-ui-library/build/components/Drawer/Drawer";
import FormItem from "react3l-ui-library/build/components/FormItem";
import InputText from "react3l-ui-library/build/components/Input/InputText";
import Select from "react3l-ui-library/build/components/Select/SingleSelect/Select";
import { utilService } from "services/common-services/util-service";
import nameof from "ts-nameof.macro";
import { userManualRepository } from "repositories/user-manual-repository";
import { SiteFilter } from "models/Site";
import { DatePicker, UploadFile } from "react3l-ui-library";
import { FileModel } from "models/FileTemplate";
import { AppUser, AppUserFilter } from "models/AppUser";
import moment from "moment";
import _ from "lodash";
import { catchError } from "rxjs/operators";
import { throwError } from "rxjs";

/* end individual import */

interface UserManualDetailDrawerProps extends DrawerProps {
  model: UserManual;
  handleChangeSingleField?: (config: {
    fieldName: string;
  }) => (value: any) => void;
  handleChangeSelectField?: (config: {
    fieldName: string;
  }) => (idValue: number, value: Model) => void;
  loading?: boolean;
}

function UserManualDetailDrawer(props: UserManualDetailDrawerProps) {
  const [translate] = useTranslation();
  const currentUserInfo: any = JSON.parse(
    localStorage.getItem("currentUserInfo")
  );

  const {
    model: _model,
    handleChangeSingleField,
    handleChangeSelectField,
    loading,
    visible,
    handleSave,
    handleCancel,
  } = props;

  const model = React.useMemo(() => {
    if (_.isNil(_model?.id)) {
      _model.createdAt = moment();
      _model.appUser = currentUserInfo;
      _model.appUserId = currentUserInfo?.id;
    }
    return _model;
  }, [_model, currentUserInfo]);

  const handleChangeFile = React.useCallback(
    (files: FileModel[]) => {
      handleChangeSelectField({
        fieldName: nameof(model.file),
      })(files[0]?.id, files[0]);
    },
    [handleChangeSelectField, model.file]
  );

  const handleRemoveFile = React.useCallback(
    (fileId: string | number) => {
      handleChangeSelectField({
        fieldName: nameof(model.file),
      })(undefined, undefined);
    },
    [handleChangeSelectField, model.file]
  );

  const handleUploadFile = React.useCallback(
    (files?: any) => {
      return userManualRepository.upload(files).pipe(
        catchError((error: any) => {
          // Extract error messages from the response
          const errorData = error?.response?.data;
          const errorMessages = errorData?.generalErrors || [];

          // Display error notification
          if (errorMessages.length > 0) {
            errorMessages.forEach((message: string) => {
              notification.error({
                message: "Lỗi tải lên",
                description: message,
                duration: 5,
                placement: "bottomRight",
              });
            });
          } else {
            // Fallback error message
            notification.error({
              message: "Lỗi tải lên",
              description: "Không thể tải lên tệp. Vui lòng thử lại.",
              duration: 5,
              placement: "bottomRight",
            });
          }

          // Re-throw the error to prevent file from being added to the list
          return throwError(() => error);
        })
      );
    },
    []
  );

  const isDisableSave = React.useMemo(() => {
        return !model?.fullName || !model?.site || !model?.file;
    }, [model?.fullName, model?.site, model?.file]);

  return (
    <Drawer
      {...props}
      visible={visible}
      handleSave={handleSave}
      handleCancel={handleCancel}
      handleClose={handleCancel}
      visibleFooter={true}
      title={
        model?.id
          ? translate("general.detail.title")
          : translate("general.actions.create")
      }
      titleButtonCancel={translate("general.actions.cancel")}
      titleButtonApply={translate("general.actions.save")}
      disableButton={isDisableSave}
    >
      {loading ? (
        <div className="loading-block">
          <img src={ASSETS_SVG + "/spinner.svg"} alt="Loading..." />
        </div>
      ) : (
        <div className="page page__detail">
          <div className="w-100 page__detail-tabs">
            <Row className="d-flex">
              <Col lg={24}>
                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col lg={24} className="m-b--sm m-t--sm">
                    <FormItem
                      validateStatus={utilService.getValidateStatus(
                        model,
                        nameof(model.fullName)
                      )}
                      message={model.errors?.fullName}
                    >
                      <InputText
                        isRequired={true}
                        label={translate("userManuals.fullName")}
                        type={0}
                        value={model.fullName}
                        placeHolder={translate(
                          "userManuals.placeholder.fullName"
                        )}
                        className={"tio-account_square_outlined"}
                        onChange={handleChangeSingleField({
                          fieldName: nameof(model.fullName),
                        })}
                      />
                    </FormItem>
                  </Col>

                  <Col lg={24} className="m-t--sm">
                    <FormItem
                      validateStatus={utilService.getValidateStatus(
                        model,
                        nameof(model.siteId)
                      )}
                      message={model.errors?.siteId}
                    >
                      <Select
                        isRequired={true}
                        label={translate("userManuals.site")}
                        type={0}
                        classFilter={SiteFilter}
                        placeHolder={translate("userManuals.placeholder.site")}
                        getList={userManualRepository.singleListSite}
                        onChange={handleChangeSelectField({
                          fieldName: nameof(model.site),
                        })}
                        value={model.site}
                      />
                    </FormItem>
                  </Col>
                  <Col lg={24} className="m-t--sm">
                    <FormItem
                      validateStatus={utilService.getValidateStatus(
                        model,
                        nameof(model.appUser)
                      )}
                      message={model.errors?.appUser}
                    >
                      <Select
                        isRequired={true}
                        label={translate("userManuals.appUser")}
                        type={0}
                        classFilter={AppUserFilter}
                        placeHolder={translate(
                          "userManuals.placeholder.appUser"
                        )}
                        getList={userManualRepository.singleListSite}
                        onChange={handleChangeSelectField({
                          fieldName: nameof(model.appUser),
                        })}
                        value={model.appUser}
                        render={(t: AppUser) => t?.displayName}
                        disabled
                      />
                    </FormItem>
                  </Col>
                  <Col lg={24} className="m-t--sm">
                    <FormItem
                      validateStatus={utilService.getValidateStatus(
                        model,
                        nameof(model.createdAt)
                      )}
                      message={model.errors?.createdAt}
                    >
                      <DatePicker
                        label={translate("userManuals.createdAt")}
                        value={model.createdAt}
                        type={0}
                        placeholder={translate(
                          "userManuals.placeholder.createdAt"
                        )}
                        disabled
                      />
                    </FormItem>
                  </Col>
                  <Col lg={24} className="m-b--sm m-t--sm">
                    <FormItem
                      validateStatus={utilService.getValidateStatus(
                        model,
                        nameof(model.file)
                      )}
                      message={model.errors?.file}
                    >
                      <div>
                        <label className={"label-status m-b--xxxs"}>
                          {translate("userManuals.placeholder.url")}
                        </label>
                        <UploadFile
                          isMultiple={false}
                          files={model?.file ? [model?.file] : []}
                          type="link"
                          maximumSize={50000000}
                          uploadFile={handleUploadFile}
                          updateList={handleChangeFile}
                          removeFile={handleRemoveFile}
                        />
                      </div>
                    </FormItem>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </Drawer>
  );
}

export default UserManualDetailDrawer;
